import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Activity, 
  PauseCircle,
  PlayCircle,
  XCircle,
  TrendingUp,
  FileText,
  Search,
  BarChart3
} from 'lucide-react';
import { authFetch } from '../utils/auth-fetch';
import { useSite } from '../contexts/SiteContext';
import { formatDistanceToNow } from 'date-fns';

interface CrawlSession {
  id: string;
  site_id: string;
  session_id: string;
  crawl_type: string;
  max_pages: number;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  started_by: string;
  started_at: string;
  completed_at?: string;
  pages_crawled: number;
  pages_extracted: number;
  pages_analyzed: number;
  error_message?: string;
  config?: any;
}

interface CrawlProgress extends CrawlSession {
  progress: number;
  estimated_remaining_minutes: number;
  elapsed_minutes: number;
  pages_per_minute: number;
  recent_pages: string[];
}

interface CrawlStats {
  total_crawls: number;
  completed_crawls: number;
  active_crawls: number;
  failed_crawls: number;
  total_pages: number;
  extracted_pages: number;
  last_crawl_date: string;
  avg_crawl_duration_minutes: number;
}

export function CrawlMonitor() {
  const { selectedSite } = useSite();
  const [crawlSessions, setCrawlSessions] = useState<CrawlSession[]>([]);
  const [activeSession, setActiveSession] = useState<CrawlProgress | null>(null);
  const [crawlStats, setCrawlStats] = useState<CrawlStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState('active');
  const [cancellingSession, setCancellingSession] = useState<string | null>(null);

  // Fetch crawl sessions
  const fetchCrawlSessions = async () => {
    if (!selectedSite) return;
    
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/crawl-sessions`);
      if (!response.ok) {
        throw new Error('Failed to fetch crawl sessions');
      }
      const data = await response.json();
      setCrawlSessions(data);
      
      // Find active session
      const active = data.find((s: CrawlSession) => s.status === 'running');
      if (active) {
        fetchActiveProgress(active.id);
      } else {
        setActiveSession(null);
      }
    } catch (err) {
      console.error('Error fetching crawl sessions:', err);
      setError('Failed to load crawl sessions');
    }
  };

  // Fetch active session progress
  const fetchActiveProgress = async (sessionId: string) => {
    if (!selectedSite) return;
    
    try {
      const response = await authFetch(
        `/api/sites/${selectedSite.id}/crawl-sessions/${sessionId}/progress`
      );
      if (response.ok) {
        const data = await response.json();
        setActiveSession(data);
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  // Fetch crawl statistics
  const fetchCrawlStats = async () => {
    if (!selectedSite) return;
    
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/crawl-stats`);
      if (response.ok) {
        const data = await response.json();
        setCrawlStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Cancel active crawl
  const cancelCrawl = async (sessionId: string) => {
    if (!selectedSite) return;
    
    setCancellingSession(sessionId);
    try {
      const response = await authFetch(
        `/api/sites/${selectedSite.id}/crawl-sessions/${sessionId}/cancel`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        // Refresh sessions
        await fetchCrawlSessions();
      } else {
        throw new Error('Failed to cancel crawl');
      }
    } catch (err) {
      console.error('Error cancelling crawl:', err);
      setError('Failed to cancel crawl session');
    } finally {
      setCancellingSession(null);
    }
  };

  // Initial load and refresh
  useEffect(() => {
    if (!selectedSite) {
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([
      fetchCrawlSessions(),
      fetchCrawlStats()
    ]).finally(() => setLoading(false));

    // Refresh every 5 seconds if there's an active session
    const interval = setInterval(() => {
      if (activeSession) {
        fetchActiveProgress(activeSession.id);
      }
      fetchCrawlSessions();
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedSite, activeSession?.id]);

  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Please select a site to view crawl monitoring</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading crawl data...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'success' | 'destructive' | 'secondary'> = {
      running: 'default',
      completed: 'success',
      failed: 'destructive',
      cancelled: 'secondary'
    };
    
    return (
      <Badge variant={variants[status] || 'default'} className="capitalize">
        {getStatusIcon(status)}
        <span className="ml-1">{status}</span>
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Crawl Monitoring</h1>
        <p className="text-gray-600 mt-2">Monitor and manage crawl sessions for {selectedSite.name}</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Statistics Overview */}
      {crawlStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Crawls</p>
                  <p className="text-2xl font-bold">{crawlStats.total_crawls}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pages</p>
                  <p className="text-2xl font-bold">{crawlStats.total_pages}</p>
                </div>
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Extracted</p>
                  <p className="text-2xl font-bold">{crawlStats.extracted_pages}</p>
                </div>
                <Search className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Duration</p>
                  <p className="text-2xl font-bold">
                    {crawlStats.avg_crawl_duration_minutes || 0}m
                  </p>
                </div>
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Crawl Session */}
      {activeSession && (
        <Card className="mb-6 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Activity className="h-5 w-5 mr-2 text-blue-500" />
                <span>Active Crawl Session</span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => cancelCrawl(activeSession.id)}
                disabled={cancellingSession === activeSession.id}
              >
                {cancellingSession === activeSession.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <PauseCircle className="h-4 w-4 mr-2" />
                    Cancel Crawl
                  </>
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Progress</span>
                <span className="font-medium">
                  {activeSession.pages_crawled} / {activeSession.max_pages} pages
                </span>
              </div>
              <Progress value={activeSession.progress} className="h-3" />
              <p className="text-xs text-gray-500 mt-1">
                {activeSession.progress.toFixed(1)}% complete
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Speed</p>
                <p className="font-medium">
                  {activeSession.pages_per_minute.toFixed(1)} pages/min
                </p>
              </div>
              <div>
                <p className="text-gray-500">Elapsed</p>
                <p className="font-medium">{activeSession.elapsed_minutes} minutes</p>
              </div>
              <div>
                <p className="text-gray-500">Est. Remaining</p>
                <p className="font-medium">
                  {activeSession.estimated_remaining_minutes} minutes
                </p>
              </div>
              <div>
                <p className="text-gray-500">Pages Analyzed</p>
                <p className="font-medium">{activeSession.pages_analyzed}</p>
              </div>
            </div>

            {activeSession.recent_pages && activeSession.recent_pages.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Recently Crawled Pages</p>
                <div className="space-y-1">
                  {activeSession.recent_pages.slice(0, 5).map((url, idx) => (
                    <div key={idx} className="text-xs text-gray-600 truncate">
                      {url}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-gray-400">
              Session ID: {activeSession.session_id}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Crawl Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Crawl History</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="failed">Failed</TabsTrigger>
            </TabsList>

            <TabsContent value={selectedTab} className="mt-4">
              <div className="space-y-2">
                {crawlSessions
                  .filter(session => 
                    selectedTab === 'all' || 
                    (selectedTab === 'active' && session.status === 'running') ||
                    (selectedTab === 'completed' && session.status === 'completed') ||
                    (selectedTab === 'failed' && session.status === 'failed')
                  )
                  .map(session => (
                    <div
                      key={session.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(session.status)}
                          <span className="text-sm font-medium">
                            {session.crawl_type === 'deep_crawl' ? 'Deep Crawl' : 'Standard Crawl'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Pages Crawled</p>
                          <p className="font-medium">
                            {session.pages_crawled} / {session.max_pages}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Extracted</p>
                          <p className="font-medium">{session.pages_extracted}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Analyzed</p>
                          <p className="font-medium">{session.pages_analyzed}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Started By</p>
                          <p className="font-medium text-xs">{session.started_by}</p>
                        </div>
                      </div>

                      {session.error_message && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                          {session.error_message}
                        </div>
                      )}

                      {session.completed_at && (
                        <div className="mt-2 text-xs text-gray-400">
                          Completed: {new Date(session.completed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}

                {crawlSessions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No crawl sessions found
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}